import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { cleanPhone, validateEmail, validatePhone } from "Shared/Form/validation";
import { Desktop } from "Shared/Responsive";
import styled, { css } from "styled-components";
import { analyticsFEA } from "src/AnalyticsFEA";
import { ApplicationStore } from "src/store";
import { updateAccessRequest } from "src/store/actions/deal";
import { personAuthSuccess } from "src/store/actions/personAuth";
import { postTo } from "src/store/dataService/routes/shared/osfetch";
import { getCountry, isLockedPricingSetup } from "src/store/selectors/account";
import { getEmail, getFirstName, getLastName, getMobilePhone } from "src/store/selectors/person";
import { getVehiclesArrayForFEA } from "src/store/selectors/vehicles";
import { getRegion } from "src/utils";
import { CountryCode } from "Types/api/Account";
import { Person } from "Types/api/Person";
import Header from "../shared/GarageHeader";
import GarageKey, { GarageKeyType } from "../shared/GarageKey";
import WaityButton from "../shared/WaityButton";
import TermsAndPrivacy from "../TermsAndPrivacy";
import "./CreateGarage.scss";
import Ribbon from "./Ribbon";
import { setSignIn } from "src/store/actions/onlineShopper/signIn";

const CreateGarageComponent = ({
    setAccessRequest,
    setPerson,
    account,
    dealId,
    email,
    firstName,
    isLockedPricing,
    lastName,
    mobilePhone,
    country,
    vehicles,
    history,
    location,
    setSignInRoute,
}: RouteComponentProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [firstNameState, setFirstName] = useState(firstName);
    const [lastNameState, setLastName] = useState(lastName);
    // In Redux, email and phone may be undefined but never empty string.
    // But in this context, the state variables are only used with text input, they start as emptystring, and validateCreateAccount makes sure they're valid
    const [garageKey, setGarageKey] = useState({ email: email || "", phone: mobilePhone || "" });
    const [garageKeyType, setGarageKeyType] = useState(email ? GarageKeyType.email : GarageKeyType.phone);
    const [optInTransactional, setOptInTransactional] = useState(true);
    const [optInMarketing, setOptInMarketing] = useState(false);
    const disabledActionButton = country === CountryCode.Canada && !optInTransactional;
    const { t } = useTranslation();

    const updateGarageKey = (type: GarageKeyType, key: string) => {
        setGarageKey({ ...garageKey, [type]: key });
    };

    const onFocus = () => {
        analyticsFEA.engine.leadFormStart(dealId, vehicles);
    };

    const onSuccess = () => {
        setErrorMessage("");

        history.push("/unlock-garage", { from: location.pathname });

        analyticsFEA.engine.leadFormFinish(dealId, vehicles);
        analyticsFEA.engine.paymentOfferSelected(dealId, vehicles);
    };

    const createAccount = async (): Promise<boolean> => {
        if (!firstNameState) {
            setErrorMessage(t("UserAccount.CreateGarage.firstNameRequiredErrorMessage"));
            return false;
        }

        if (!lastNameState) {
            setErrorMessage(t("UserAccount.CreateGarage.lastNameRequiredErrorMessage"));
            return false;
        }

        // For some reason validatePhone and validateEmail are ok with '', obviously we aren't
        if (garageKeyType === GarageKeyType.phone && (!garageKey.phone || !validatePhone(garageKey.phone))) {
            setErrorMessage(t("UserAccount.CreateGarage.validPhoneErrorMessage"));
            return false;
        }

        if (garageKeyType === GarageKeyType.email && (!garageKey.email || !validateEmail(garageKey.email))) {
            setErrorMessage(t("UserAccount.CreateGarage.validEmailErrorMessage"));
            return false;
        }

        if (country === CountryCode.Canada && !optInTransactional) {
            setErrorMessage(t("UserAccount.CreateGarage.agreeCommunicationsErrorMessage"));
            return false;
        }

        const response = await postTo(`/deals/${dealId}/person/auth`, {
            person: {
                first_name: firstNameState,
                last_name: lastNameState,
                ...(garageKeyType === GarageKeyType.email && { email: garageKey.email }),
                ...(garageKeyType === GarageKeyType.phone && { mobile_phone: cleanPhone(garageKey.phone) }),
                preferences: {
                    ...(country === CountryCode.US && { tcpa_consent: true }),
                    ...(country === CountryCode.Canada && { optInMarketing, optInTransactional }),
                },
            },
            // API needs 'sms' GarageKeyType calls it 'phone'
            send_pin_to: garageKeyType === GarageKeyType.email ? "email" : "sms",
        });

        const data = await response.json();

        if (!response.ok) {
            setErrorMessage(data.message || t("UserAccount.CreateGarage.genericErrorMessage"));
            return false;
        }

        setPerson(data as Person);
        setAccessRequest({
            garageKey: garageKey[garageKeyType],
            garageKeyType,
            isNew: true,
        });

        setErrorMessage("");
        return true;
    };

    return (
        <div className="Garage">
            <Header
                text={isLockedPricing ? t("UserAccount.CreateGarage.unlock") : t("UserAccount.CreateGarage.welcome")}
            />
            <Desktop>
                <Ribbon />
            </Desktop>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <div className="Garage__form--label-container">
                <label className="Garage__form--label">{t("UserAccount.CreateGarage.label")}</label>
                <p className="Garage__form--sublabel">{t("UserAccount.CreateGarage.sublabel")}</p>
            </div>
            <div className="Garage__form">
                <input
                    className="Garage__form--input"
                    placeholder={t("UserAccount.CreateGarage.firstName")}
                    name="firstName"
                    value={firstNameState}
                    onChange={(e) => setFirstName(e.target.value)}
                    onFocus={onFocus}
                />
                <input
                    className="Garage__form--input"
                    name="lastName"
                    placeholder={t("UserAccount.CreateGarage.lastName")}
                    value={lastNameState}
                    onChange={(e) => setLastName(e.target.value)}
                    onFocus={onFocus}
                />
            </div>
            <div className="Garage__form--label-container">
                <label className="Garage__form--label">{t("UserAccount.CreateGarage.garageKey")}</label>
                <p className="Garage__form--sublabel">{t("UserAccount.CreateGarage.comeBack")}</p>
            </div>
            <div className="Garage__form">
                <GarageKey
                    updateKey={updateGarageKey}
                    updateKeyType={setGarageKeyType}
                    styles={garageKeyStyles}
                    garageKeyType={garageKeyType}
                    selectedGarageKey={garageKey[garageKeyType]}
                />
            </div>

            <hr className="Garage__form--divider" />

            <ButtonContainer>
                <WaityButton
                    onClick={createAccount}
                    onSuccess={onSuccess}
                    text={account.account_disclaimers.garage_open_cta}
                    disabled={disabledActionButton}
                />
            </ButtonContainer>

            <TermsAndPrivacy
                region={country}
                account={account}
                optInMarketing={optInMarketing}
                optInTransactional={optInTransactional}
                onAgreeTransactionalClick={setOptInTransactional}
                onAgreePromotionalClick={setOptInMarketing}
            />

            <LoginContainer>
                {t("UserAccount.CreateGarage.alreadyGarage")}{" "}
                <StyledLink to="/access-my-garage" onClick={() => setSignInRoute({ routeSignIn: true })}>
                    {t("UserAccount.CreateGarage.signIn")}
                </StyledLink>
            </LoginContainer>
        </div>
    );
};

const ErrorMessage = styled("div")`
    border: 1px solid ${(props) => props.theme.palette.error.main};
    color: ${(props) => props.theme.palette.error.main};
    font-weight: bold;
    margin: 0.4em auto;
    padding: 0.4em 1.5em;
    text-align: center;
`;

const StyledLink = styled(Link)`
    color: ${(props) => props.theme.palette.common.black};
    text-decoration: underline;
`;

const ButtonContainer = styled("div")`
    margin-top: ${(props) => props.theme.spacing.unit * 2}px;
`;

const LoginContainer = styled("div")`
    color: ${(props) => props.theme.palette.common.black};
    font-size: 1em;
    font-weight: 700;
    text-align: center;
`;

const garageKeyStyles = {
    inputBox: css`
        font-size: 16px;
    `,
};

function mapStateToProps(state: ApplicationStore, props: any) {
    const { account, deal } = state;

    return {
        account,
        dealId: deal.uuid || "",
        email: getEmail(state),
        mobilePhone: getMobilePhone(state),
        firstName: getFirstName(state),
        isLockedPricing: isLockedPricingSetup(state),
        lastName: getLastName(state),
        region: getRegion(account),
        country: getCountry(state),
        vehicles: getVehiclesArrayForFEA(state),
    };
}

const mapDispatchToProps = {
    setAccessRequest: updateAccessRequest,
    setPerson: personAuthSuccess,
    setSignInRoute: setSignIn,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateGarageComponent);
